import { MatchBoardActions, MatchBoardActionTypes } from '../actions/matchboard.actions';
import { Price } from '../../../models/price';
import { SwapHistory, SwapHistoryColumns } from '../../../models/swap-history.model';
import { TradeHistoryColumns } from '../../../models/trade-history.model';
import { Order } from '../../../models/orderbook.model';
import { CurrencyDto } from '../../../models/CurrencyDto';
import { User } from '../../../models/user';
import { OrderMatchedState } from '../../../models/OrderMatchedState';

export interface MatchBoardState {
  topOrders: {[k: string]: {[d: string]: Array<Price>}};
  spotPrices: {[k: string]: number};
  graphData: {[k: string]: Array<number>};
  swapHistory: SwapHistory[];
  trades: TradeHistoryColumns[];
  orders: {[k: number]: Order};
  currencyPairs: CurrencyDto[];
  lastTradePrices: {[k: string]: number};
  onBehalfUsers: Array<User>;
  selectedOnBehalfUser: string;
  currencyMappings: Array<any>;
  allOrders: {[k: number]: Order};
  abosPositions: Array<any>;
  isSuperUser: boolean;
}

export const initialState: MatchBoardState = {
  topOrders: {},
  spotPrices: {},
  graphData: {},
  swapHistory: [],
  trades: [],
  orders: {},
  currencyPairs: [],
  lastTradePrices: {},
  onBehalfUsers: [],
  selectedOnBehalfUser: null,
  currencyMappings: [],
  allOrders: [],
  abosPositions: [],
  isSuperUser: false
};

export function MatchBoardReducer(state: MatchBoardState = initialState, action: MatchBoardActions): MatchBoardState {
  switch (action.type) {
    case MatchBoardActionTypes.InitState: {
      return {
        ...state,
        topOrders: action.state.topOrders,
        spotPrices: action.state.spotPrices,
        graphData: action.state.graphData,
        swapHistory: action.state.swapHistory,
        // tabs: action.state.tabs ? mapFunctionStringKey<TabView>(action.state.tabs, 'name') : null,
        trades: action.state.trades,
        orders: action.state.orders ? {...mapOrders(action.state.orders as any)} : {},
        currencyPairs: action.state.currencyPairs,
        lastTradePrices: action.state.lastTradePrices,
        onBehalfUsers: action.state.onBehalfUsers,
        selectedOnBehalfUser: action.state.selectedOnBehalfUser,
        currencyMappings: action.state.currencyMappings,
        allOrders: action.state.isSuperUser ? {...mapOrders(action.state.allOrders as any)} : null,
        abosPositions: action.state.abosPositions,
        isSuperUser: action.state.isSuperUser
      };
    }
    case MatchBoardActionTypes.NewOrders: {
      if (!state.isSuperUser) {
        return {
          ...state,
          orders: {
            ...state.orders,
            ...mapOrders(action.orders)
          }
        };
      }
      return {
        ...state,
        orders: {
          ...state.orders,
          ...mapOrders(action.orders.filter(o => o.userId === state.selectedOnBehalfUser))
        },
        allOrders: {
          ...state.allOrders,
          ...mapOrders(action.orders)
        }
      };
    }
    case MatchBoardActionTypes.OrdersMatched: {
      const ordersCopy = removeProperties(state.orders, action.payload.ordersToRemove);
      if (!state.isSuperUser) {
        return {
          ...state,
          orders: {
            ...ordersCopy,
            ...mapOrders(Object.values(action.payload.stateChanges).filter(s => s.order.status === 'NEW').map(s => s.order))
          },
          trades: [
            ...mapToOneArray<TradeHistoryColumns>(Object.values(action.payload.stateChanges), 'trades'),
            ...state.trades
          ],
          swapHistory: [
            ...mapToOneArray<SwapHistoryColumns>(Object.values(action.payload.stateChanges), 'swapHistory'),
            ...state.swapHistory
          ]
        };
      }

      const selectedOnBehalfUserOrders: Array<Order> = [];

      const updatedOrders: Array<Order> = new Array<Order>();

      Object.values(action.payload.stateChanges).forEach(s => {
        Object.values(s).forEach(v => {
          if (v.order.status === 'NEW') {
            updatedOrders.push(v.order);
          }
          if (v.order.userId === state.selectedOnBehalfUser) {
            selectedOnBehalfUserOrders.push(v.order);
          }
        });
      });

      const allOrders = removeProperties(state.allOrders, action.payload.ordersToRemove);

      if (selectedOnBehalfUserOrders.length > 0) {
        return {
          ...state,
          orders: {
            ...ordersCopy,
            ...mapOrders(selectedOnBehalfUserOrders.filter(o => o.status === 'NEW'))
          },
          allOrders: {
            ...allOrders,
            ...mapOrders(updatedOrders)
          },
          swapHistory: [
            ...mapSwapHistoryForSuperUser(action.payload.stateChanges, state.selectedOnBehalfUser),
            ...state.swapHistory
          ],
          trades: [
            ...mapTradesForSuperUser(action.payload.stateChanges, state.selectedOnBehalfUser),
            ...state.trades
          ]
        };
      }

      return {
        ...state,
        allOrders: {
          ...allOrders,
          ...mapOrders(updatedOrders)
        }
      };
    }
    case MatchBoardActionTypes.StateChanged: {
      return {
        ...state,
        topOrders: action.payload.topOrders,
        graphData: action.payload.graphData ? action.payload.graphData : state.graphData,
        lastTradePrices: action.payload.lastTradePrices ? action.payload.lastTradePrices : state.lastTradePrices
      };
    }
    case MatchBoardActionTypes.PositionsUpdate: {
      return {
        ...state,
        abosPositions: action.payload
      };
    }
    case MatchBoardActionTypes.RemoveOrders: {
      if (!state.isSuperUser) {
        return {
          ...state,
          orders: removeProperties(state.orders, action.orderIds)
        };
      }
      return {
        ...state,
        orders: removeProperties(state.orders, action.orderIds),
        allOrders: removeProperties(state.allOrders, action.orderIds)
      };
    }
    case MatchBoardActionTypes.SelectOnBehalfUser: {
      return {
        ...state,
        selectedOnBehalfUser: action.selectOnBehalfUser.selectedUser,
        orders: mapOrders(action.selectOnBehalfUser.orders),
        trades: action.selectOnBehalfUser.trades,
        swapHistory: action.selectOnBehalfUser.swapHistory,
        abosPositions: action.selectOnBehalfUser.abosPositions
      };
    }
    case MatchBoardActionTypes.UpdateSpotPrices: {
      return {
        ...state,
        spotPrices: action.payload
      };
    }
    case MatchBoardActionTypes.UpdateGraphData: {
      return {
        ...state,
        graphData: action.payload
      };
    }
    case MatchBoardActionTypes.CurrencyPairsUpdate: {
      let copy = [...state.currencyPairs];
      if (action.payload.deleteCurrencyPairs && action.payload.deleteCurrencyPairs.length > 0) {
        copy = copy.filter(cp => !action.payload.deleteCurrencyPairs.includes(cp.sixLetterCode));
      }

      if (action.payload.updateCurrencyPairs && action.payload.updateCurrencyPairs.length > 0) {
        copy = copy.map(cp => {
          const update = action.payload.updateCurrencyPairs.find(ccy => ccy.sixLetterCode === cp.sixLetterCode);
          if (update) {
            return {
              ...cp,
              swapPricePrecision: update.swapPricePrecision,
              spotPricePrecision: update.spotPricePrecision,
              spotPriceFactor: update.spotPriceFactor,
              displaySpotPricePrecision: update.displaySpotPricePrecision,
              swapPriceFactor: update.swapPriceFactor,
              rounding: update.rounding
            };
          }
          return cp;
        });
      }

      if (action.payload.newCurrencyPairs && action.payload.newCurrencyPairs.length > 0) {
        copy = [
          ...copy,
          ...action.payload.newCurrencyPairs
        ];
      }

      return {
        ...state,
        currencyPairs: copy
      };
    }
    default: return state;
  }
}

const removeProperty = prop => ({ [prop]: _, ...rest }) => rest;

function removeProperties(obj: any, propertiesToRemove: Array<any>): typeof obj {
  let copy = {...obj};
  for (const prop of propertiesToRemove) {
    const fn = removeProperty(prop);
    copy = fn(copy);
  }
  return copy;
}

function mapSwapHistoryForSuperUser(states: any, userId: string): Array<SwapHistoryColumns> {
  const arr: Array<SwapHistoryColumns> = new Array<SwapHistoryColumns>();
  Object.values(states).forEach(u => {
    Object.values(u).forEach(o =>  {
      if (o.order.userId === userId) {
        o.swapHistory.forEach(item => {
          arr.push(item);
        });
      }
    });
  });
  return arr;
}

function mapTradesForSuperUser(states: any, userId: string): Array<TradeHistoryColumns> {
  const arr: Array<TradeHistoryColumns> = new Array<TradeHistoryColumns>();

  Object.values(states).forEach(u => {
    Object.values(u).forEach(o =>  {
      if (o.order.userId === userId) {
        o.trades.forEach(item => {
          arr.push(item);
        });
      }
    });
  });
  return arr;
}

function mapToOneArray<V>(states: Array<OrderMatchedState>, key: string): Array<V> {
  const arr: Array<V> = new Array<V>();
  states.forEach(s => {
    s[key].forEach(item => {
      arr.push(item);
    });
  });
  return arr;
}

function mapOrders(orders: Array<Order>): {[k: number]: Order} {
  const orderMap = {};
  orders.forEach(o => orderMap[o.id] = o);
  return orderMap;
}

// function mapFunctionStringKey<V>(items: Array<V>, key: string): {[k: string]: V} {
//   const res = {};
//   items.forEach(o => res[o[key]] = o);
//   return res;
// }
//
// function mapFunctionNumberKey<V>(items: Array<V>, key: string): {[k: number]: V} {
//   const res = {};
//   items.forEach(o => res[o[key]] = o);
//   return res;
// }

export const getSpotPrices = (state: MatchBoardState) => state.spotPrices;
export const getTopOrders = (state: MatchBoardState) => state.topOrders;
export const getCurrencyMappings = (state: MatchBoardState) => state.currencyMappings;
export const getGraphData = (state: MatchBoardState) => state.graphData;
export const getSwapHistory = (state: MatchBoardState) => state.swapHistory;
export const getTrades = (state: MatchBoardState) => state.trades;
export const getOrders = (state: MatchBoardState) => state.orders;
export const getAllOrders = (state: MatchBoardState) => state.allOrders;
export const getLastTradePrices = (state: MatchBoardState) => state.lastTradePrices;
export const getCurrencyPairs = (state: MatchBoardState) => state.currencyPairs;
export const getOnBehalfUsers = (state: MatchBoardState) => state.onBehalfUsers;
export const getSelectedOnBehalfUser = (state: MatchBoardState) => state.selectedOnBehalfUser;
export const getAbosPositions = (state: MatchBoardState) => state.abosPositions;
export const getIsSuperUser = (state: MatchBoardState) => state.isSuperUser;
